import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up function
  const signup = async (email, password, userData) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update display name
    await updateProfile(user, {
      displayName: userData.role === 'client'
        ? `${userData.firstName} ${userData.lastName}`
        : userData.salonName
    });

    // Create user profile in Firestore based on role
    let userDoc;
    
    if (userData.role === 'client') {
      // Client profile - minimal data needed
      userDoc = {
        uid: user.uid,
        email: user.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        role: 'client',
        createdAt: new Date(),
        // Keep some fields for compatibility but mark as client
        salonName: `${userData.firstName} ${userData.lastName}`,
        ownerName: `${userData.firstName} ${userData.lastName}`,
        category: 'Client',
        description: 'Compte client',
        photos: [],
        services: [],
        isPublic: false // Clients don't need public profiles
      };
    } else {
      // Professional/salon profile - full data
      userDoc = {
        uid: user.uid,
        email: user.email,
        salonName: userData.salonName,
        ownerName: userData.ownerName,
        phone: userData.phone,
        address: userData.address,
        city: userData.city,
        region: userData.region,
        category: userData.category,
        description: userData.description || '',
        photos: [],
        services: [],
        schedule: {
          monday: { open: '09:00', close: '18:00', closed: false },
          tuesday: { open: '09:00', close: '18:00', closed: false },
          wednesday: { open: '09:00', close: '18:00', closed: false },
          thursday: { open: '09:00', close: '18:00', closed: false },
          friday: { open: '09:00', close: '18:00', closed: false },
          saturday: { open: '09:00', close: '17:00', closed: false },
          sunday: { open: '10:00', close: '16:00', closed: true }
        },
        subscription: {
          plan: 'trial',
          startDate: new Date(),
          endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000), // 15 days trial
          active: true
        },
        role: 'salon',
        createdAt: new Date(),
        isPublic: true
      };
    }

    await setDoc(doc(db, 'users', user.uid), userDoc);
    
    // Send email verification
    await sendEmailVerification(user);
    
    return userCredential;
  };

  // Sign in function
  const signin = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Sign out function
  const logout = () => {
    return signOut(auth);
  };

  // Reset password function
  const resetPassword = async (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Get user profile from Firestore
  const getUserProfile = async (uid) => {
    try {
      // Fetching user profile
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        // User profile found
        return userDoc.data();
      } else {
        console.log('No user profile found for UID:', uid);
        return null;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  };

  // Force refresh user profile after email verification
  const refreshUserProfile = async () => {
    if (currentUser && currentUser.emailVerified) {
      console.log('Refreshing user profile after email verification...');
      const profile = await getUserProfile(currentUser.uid);
      setUserProfile(profile);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Auth state changed
      if (user) {
        // Toujours recharger l'utilisateur pour avoir le statut de vérification le plus récent
        await user.reload();
        const refreshedUser = auth.currentUser;
        
        setCurrentUser(refreshedUser);
        
        // Toujours essayer de charger le profil existant d'abord
        let profile = await getUserProfile(refreshedUser.uid);
        
        // Si l'email est vérifié, utiliser le profil
        if (refreshedUser && refreshedUser.emailVerified) {
          // Si aucun profil n'existe ET que c'est un nouvel utilisateur (créé récemment)
          if (!profile) {
            const userCreationTime = new Date(refreshedUser.metadata.creationTime);
            const now = new Date();
            const timeDiff = now - userCreationTime;
            const hoursDiff = timeDiff / (1000 * 60 * 60);
            
            // Seulement créer un nouveau profil si l'utilisateur a été créé récemment (moins de 24h)
            if (hoursDiff < 24) {
              console.log('Creating missing user profile for new user...');
              
              // Déterminer le rôle basé sur le displayName ou d'autres indices
              // Si le displayName contient des mots comme "Client" ou ressemble à un nom complet, c'est probablement un client
              const displayName = refreshedUser.displayName || '';
              const isClient = displayName.includes('Client') ||
                              (displayName.split(' ').length >= 2 && !displayName.includes('Salon'));
              
              const basicProfile = {
                uid: refreshedUser.uid,
                email: refreshedUser.email,
                salonName: isClient ? displayName : (refreshedUser.displayName || 'Mon Salon'),
                ownerName: isClient ? displayName : (refreshedUser.displayName || 'Propriétaire'),
                phone: '',
                address: '',
                city: '',
                region: '',
                category: isClient ? 'Client' : 'Salon de coiffure',
                description: isClient ? 'Compte client' : '',
                photos: [],
                services: [],
                schedule: isClient ? {} : {
                  monday: { open: '09:00', close: '18:00', closed: false },
                  tuesday: { open: '09:00', close: '18:00', closed: false },
                  wednesday: { open: '09:00', close: '18:00', closed: false },
                  thursday: { open: '09:00', close: '18:00', closed: false },
                  friday: { open: '09:00', close: '18:00', closed: false },
                  saturday: { open: '09:00', close: '17:00', closed: false },
                  sunday: { open: '10:00', close: '16:00', closed: true }
                },
                // Nouveau système d'abonnement compatible avec Stripe (seulement pour les salons)
                subscriptionStatus: isClient ? null : 'trial',
                subscriptionType: isClient ? null : null,
                subscriptionStartDate: isClient ? null : new Date(),
                subscriptionEndDate: isClient ? null : new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
                trialStatus: isClient ? null : true,
                stripeCustomerId: null,
                stripeSessionId: null,
                lastPaymentDate: null,
                lastPaymentFailure: null,
                // Ancien système pour compatibilité (seulement pour les salons)
                subscription: isClient ? null : {
                  plan: 'trial',
                  startDate: new Date(),
                  endDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
                  active: true
                },
                role: isClient ? 'client' : 'salon',
                createdAt: new Date(),
                isPublic: !isClient // Les clients n'ont pas de profil public
              };
              
              try {
                await setDoc(doc(db, 'users', refreshedUser.uid), basicProfile);
                profile = basicProfile;
                console.log('Basic profile created successfully for new user');
              } catch (error) {
                console.error('Error creating basic profile:', error);
              }
            } else {
              console.log('User is old but has no profile - not creating new profile to avoid overwriting data');
            }
          }
          
          setUserProfile(profile);
        } else {
          // Si l'email n'est pas vérifié, ne pas charger le profil mais le garder en mémoire
          // pour éviter de perdre les données existantes
          if (!profile) {
            setUserProfile(null);
          }
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    signin,
    logout,
    resetPassword,
    getUserProfile,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};